import { DayKey, HourData } from 'components/restaurantInformation/components/types';
import dayjs from 'dayjs';
import {
    OrderInAdvanceCustomSettings,
    OrderInAdvanceSameDaySettings,
    OrderInAdvanceStockSource,
    TimeRange,
} from 'store/menuDetailsStore/reducer';

const DAY_KEYS: DayKey[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
];

export function getNextWorkingDayLabel(
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined,
    fromDate: Date = new Date()
): string | null {
    for (let offset = 1; offset <= DAY_KEYS.length; offset += 1) {
        const date = dayjs(fromDate).add(offset, 'day');
        const day = DAY_KEYS[date.day()];
        if ((workingHours?.[day] ?? []).length > 0) {
            return date.format('ddd DD.MM');
        }
    }
    return null;
}

export function formatPickupDate(date?: string | null): string | null {
    if (!date) return null;
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format('ddd DD.MM') : null;
}

export function getSizeOrderInAdvanceEnabled(settings?: { isEnabled?: boolean } | null) {
    return settings?.isEnabled ?? false;
}

function getDefaultOfferSettings(
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined
) {
    const workingHoursRange = getTakeAwayPickupRange(workingHours);
    return {
        isEnabled: false,
        isBaseSizeEnabled: true,
        discountPercent: '',
        orderingFollowsWorkingHours: true,
        pickupFollowsWorkingHours: true,
        orderingOffsetHours: 0,
        stockSource: OrderInAdvanceStockSource.Main,
        stockQuantity: null,
        orderingWindow: workingHoursRange,
        pickupWindow: workingHoursRange,
    };
}

export function getDefaultSameDayOffer(
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined
): OrderInAdvanceSameDaySettings {
    return getDefaultOfferSettings(workingHours);
}

export function getDefaultCustomOffer(
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined
): OrderInAdvanceCustomSettings {
    return {
        ...getDefaultOfferSettings(workingHours),
        daySettings: [],
    };
}

export function getInitialOfferWindow(
    saved: TimeRange | null | undefined,
    followsWorkingHours: boolean,
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined
): TimeRange {
    const range = getTakeAwayPickupRange(workingHours);
    if (followsWorkingHours) {
        return range;
    }
    return {
        from: saved?.from ?? null,
        to: saved?.to ?? null,
    };
}

export function clampTimeRangeToWorkingHours(
    range: TimeRange | null | undefined,
    workingHours: TimeRange
): TimeRange {
    if (isFullDayTimeRange(workingHours)) {
        return { from: range?.from ?? null, to: range?.to ?? null };
    }
    if (isFullDayTimeRange(range) && workingHours.from != null && workingHours.to != null) {
        return { ...workingHours };
    }
    return {
        from: clampTimeToBounds(range?.from, workingHours.from, workingHours.to),
        to: clampTimeToBounds(range?.to, workingHours.from, workingHours.to),
    };
}

function clampTimeToBounds(
    value: number | string | null | undefined,
    minimum: number | null,
    maximum: number | null
): number | null {
    const parsed = parseWorkingHourTime(value);
    if (parsed === null || minimum === null || maximum === null) {
        return parsed;
    }
    const minutes = toMinutes(parsed);
    return fromMinutes(Math.max(toMinutes(minimum), Math.min(toMinutes(maximum), minutes)));
}

export function getDiscountedPrice(
    standardPrice: number | string,
    discountPercent: number | string
) {
    const price = Number(standardPrice);
    const discount = Number(discountPercent);
    if (!Number.isFinite(price)) {
        return 0;
    }
    if (!Number.isFinite(discount) || discount <= 0 || discount > 100) {
        return price;
    }
    return Math.round(price * (1 - discount / 100) * 100) / 100;
}

export function getOrderingStartWithOffset(
    range: TimeRange,
    offsetHours: number | string
): number | null {
    const rangeStart = parseWorkingHourTime(range.from);
    if (rangeStart === null) {
        return null;
    }
    const offset = Number(offsetHours);
    const safeOffset = Number.isFinite(offset) ? Math.max(0, Math.min(12, offset)) : 0;
    const startMinutes = toMinutes(rangeStart);
    return fromMinutes(Math.max(0, startMinutes - safeOffset * 60));
}

export function isFullDayTimeRange(range?: Partial<TimeRange> | null) {
    return range?.from === 0 && range?.to === 0;
}

export function isTimeRangeValid(range?: Partial<TimeRange> | null) {
    return (
        range?.from != null && range.to != null &&
        (isFullDayTimeRange(range) || range.to > range.from)
    );
}

export function parseTimeToMinutes(value: number | string): number | null {
    let hours: number;
    let minutes: number;

    if (typeof value === 'string' && value.includes(':')) {
        const match = /^(\d{1,2}):(\d{2})$/.exec(value);
        if (!match) {
            return null;
        }
        hours = Number(match[1]);
        minutes = Number(match[2]);
    } else {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
            return null;
        }
        hours = Math.floor(numericValue);
        const decimalMinutes = (numericValue - hours) * 100;
        minutes = Math.round(decimalMinutes);
        if (Math.abs(decimalMinutes - minutes) > 1e-7) {
            return null;
        }
    }

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return null;
    }
    return hours * 60 + minutes;
}

export function isOfferWindowConfigured(
    followsWorkingHours: boolean | undefined,
    window?: Partial<TimeRange> | null
) {
    return !!followsWorkingHours || (window?.from != null && window?.to != null);
}

export function hasOwnOrSizeError(errors: Record<string, unknown>, field: string) {
    const sizes = errors.additionalSizes;
    return !!errors[field] || (Array.isArray(sizes) && sizes.some((size) => !!size?.[field]));
}

// Position of a day+time within the weekly cycle (Sunday 00:00 = 0), matching the backend's
// FromDay/From-ToDay/To weekly window so a window can be checked for being zero-length.
export function getWeekMinutes(day: string | null | undefined, time: number | null | undefined) {
    if (!day || time == null) {
        return null;
    }
    const dayIndex = DAY_KEYS.indexOf(day.toLowerCase() as DayKey);
    if (dayIndex < 0) {
        return null;
    }
    return dayIndex * 24 * 60 + toMinutes(time);
}

function toMinutes(value: number) {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 100);
    return hours * 60 + minutes;
}

// Inverse of toMinutes: converts minutes-since-midnight back to the app's HH.MM time encoding.
function fromMinutes(totalMinutes: number) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours + minutes / 100;
}

export function getTodayPickupHoursCaption(
    workingHours: Partial<Record<DayKey, HourData[]>> | null | undefined,
    pickupRange: TimeRange
): string | null {
    const todaySlots = (workingHours?.[DAY_KEYS[new Date().getDay()]] ?? [])
        .map((slot) => ({
            from: parseWorkingHourTime(slot?.from),
            to: parseWorkingHourTime(slot?.to),
        }))
        .filter(
            (slot): slot is { from: number; to: number } => slot.from !== null && slot.to !== null
        );
    const isDifferentFromRange =
        todaySlots.length > 1 ||
        (todaySlots.length === 1 &&
            (todaySlots[0].from !== pickupRange.from || todaySlots[0].to !== pickupRange.to));
    if (!isDifferentFromRange) {
        return null;
    }
    return todaySlots
        .map((slot) => `${formatPickupTime(slot.from)} - ${formatPickupTime(slot.to)}`)
        .join(', ');
}

export function formatPickupTime(value: number): string {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 100);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function formatPickupTimeslot(slot: { from: number; to: number }): string {
    const from = formatPickupTime(slot.from);
    return slot.from === slot.to
        ? from
        : `${from}-${formatPickupTime(slot.to)}`;
}

export function isPickupDateToday(pickupDate?: string | null): boolean {
    if (!pickupDate) {
        return false;
    }
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate()
    ).padStart(2, '0')}`;
    return pickupDate.slice(0, 10) === today;
}

export function parseWorkingHourTime(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
}

export function getTakeAwayPickupRange(
    workingHours?: Partial<Record<DayKey, HourData[]>> | null
): TimeRange {
    const slots = Object.keys(workingHours ?? {}).reduce<HourData[]>(
        (all, day) => all.concat(workingHours?.[day as DayKey] ?? []),
        []
    );
    return getWorkingHoursRange(slots);
}

export function getWorkingHoursRange(
    slots: Array<{ from: number | string | null; to: number | string | null }>
): TimeRange {
    if (
        slots.some((slot) =>
            isFullDayTimeRange({
                from: parseWorkingHourTime(slot?.from),
                to: parseWorkingHourTime(slot?.to),
            })
        )
    ) {
        return { from: 0, to: 0 };
    }
    return slots.reduce<TimeRange>(
        (range, slot) => {
            const from = parseWorkingHourTime(slot?.from);
            const to = parseWorkingHourTime(slot?.to);
            return {
                from:
                    from !== null && (range.from === null || from < range.from) ? from : range.from,
                to: to !== null && (range.to === null || to > range.to) ? to : range.to,
            };
        },
        { from: null, to: null }
    );
}

export function getTodayWorkingHours(
    workingHours?: Partial<Record<DayKey, HourData[]>> | null,
    date: Date = new Date()
): TimeRange[] {
    const today = DAY_KEYS[date.getDay()];
    return (workingHours?.[today] ?? [])
        .map((slot) => ({
            from: parseWorkingHourTime(slot?.from),
            to: parseWorkingHourTime(slot?.to),
        }))
        .filter((slot): slot is TimeRange => slot.from !== null && slot.to !== null);
}
